import { MenuItemButton } from '@/components/layout/DesktopNavbar';
import {
    roundedClassname,
    roundedDropdownClassname,
    xplorSpotTitle,
    xplorTraderTitle
} from '@/helpers/environmentHelper';
import { useAppDispatch } from '@/state/hooks';
import { setXplorSpotWorkspaceTabLayout, setXplorTraderWorkspaceTabLayout } from '@/state/reducers/workspaceSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { defaultXplorSpotLayout, XplorSpotModules } from '@/utils/hooks/useXplorSpotLayout';
import { defaultXplorTraderLayout, XplorTraderModules } from '@/utils/hooks/useXplorTraderLayout';
import { Menu, Transition } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useCallback, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { LuBarChart3, LuLayers, LuTrendingUp } from 'react-icons/lu';

export interface StartWithATemplateProps {
    tabList: string[];
    tabIcon: Record<XplorSpotModules & XplorTraderModules, IconType>;
    platform: 'xplortrader' | 'xplorspot';
    onClick?: (tab) => void;
}

const StartWithATemplate = ({ platform }: StartWithATemplateProps) => {
    const dispatch = useAppDispatch();
    const lang = useLanguage();

    const templateList = useMemo(() => {
        const list = [
            {
                name: xplorSpotTitle,
                Icon: LuTrendingUp
            },
            {
                name: xplorTraderTitle,
                Icon: LuBarChart3
            }
        ];

        if (platform === 'xplorspot') return [list[0]];
        else return [list[1]];
    }, [platform]);

    const startWithATemplateToXplorSpotWorkspace = useCallback(() => {
        const layout = defaultXplorSpotLayout();
        dispatch(setXplorSpotWorkspaceTabLayout({ id: 'current', layout: layout.desktop, screen: 'desktop' }));
    }, [dispatch]);

    const startWithATemplateToXplorTraderWorkspace = useCallback(() => {
        const layout = defaultXplorTraderLayout();
        dispatch(setXplorTraderWorkspaceTabLayout({ id: 'current', layout: layout.desktop, screen: 'desktop' }));
    }, [dispatch]);

    return (
        <Menu as="div" className={cn('relative inline-block text-left', roundedClassname)}>
            <Menu.Button
                type="button"
                className={cn(
                    'relative flex gap-1 text-sm items-center transition duration-150 ease-in-out justify-between h-full rounded-md text-brand-text bg-brand-neutral-700 hover:bg-brand-neutral-600 py-2 pl-2 pr-3',
                    roundedClassname,
                    {}
                )}>
                <div className="px-0.5">
                    <LuLayers className={cn('w-4 h-4')} />
                </div>
                <div>{lang.workspace.startWithATemplate}</div>
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items
                    className={cn(
                        'absolute z-10 mt-4 w-48 origin-top-right divide-y divide-white/10 bg-brand-background-dark border border-brand-neutral-700 overflow-hidden right-0',
                        roundedDropdownClassname
                    )}>
                    {templateList.map((template) => (
                        <MenuItemButton
                            key={template.name}
                            label={template.name}
                            Icon={template.Icon}
                            onClick={() => {
                                if (platform === 'xplorspot') {
                                    startWithATemplateToXplorSpotWorkspace();
                                } else {
                                    startWithATemplateToXplorTraderWorkspace();
                                }
                            }}
                        />
                    ))}
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

export default StartWithATemplate;
